import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Stack, Typography, useTheme } from '@mui/material'

import { ProductImagePlaceholder } from '@repo/ui'
import { FadeInImage } from 'components/UI/FadeInImage'
import { ROUTES } from 'routes'
import { StatusBadge } from 'components/pages/certificates/StatusBadge'
import { PENDING_OWNERSHIP_CHIP } from 'utils/displayStatusColorMapper'
import { formatedDate, parseApiDate } from 'utils/date'
import { formatSku } from 'utils/formatSku'
import type { PendingSale } from 'types'
import { innerRadius, surfaceRadius } from 'settings/brandRadius'

export interface PendingSaleTeaserProps {
  sale: PendingSale
  /** Two-per-row grid presentation, matching `CertificatesTeaser`'s `compact`. */
  compact?: boolean
}

/**
 * VRG-455 — an item the buyer has bought, waiting on their scan.
 *
 * ## It sits IN the list, as a card, on purpose
 *
 * Not a pinned banner above it. VERA-304 removed pinned transfer banners from
 * this page after four of them became a ~100px wall that pushed every actual
 * certificate below the fold — and a banner reads as a notification about
 * something elsewhere, which is the wrong idea here. The buyer owns this thing;
 * it belongs among the things they own.
 *
 * ## PRODUCT-LEVEL, and that is the whole design
 *
 * Product name, images, purchase date, and an unverified state. Nothing else,
 * because nothing else exists: no certificate is bound until the scan, so there
 * is no tag, no serial and no item-specific media to show. That constraint is
 * not a limitation to work around — it is what keeps this indistinguishable
 * from an ordinary incoming transfer. A pre-claim transfer row shows exactly
 * this much too, so the buyer has nothing to compare against and no new concept
 * to learn.
 *
 * ⚠️ If you are here to add a field, check it is a property of the PRODUCT or of
 * the sale. The first item-specific field on this card is the moment the two
 * flows visibly diverge.
 *
 * ## The card OPENS the claim; it does not host it
 *
 * Tapping this navigates to `pages/PendingSalePage`, exactly as every other row
 * in the grid opens its certificate. The "Scan to claim ownership" button used
 * to live in this column and rendered CLIPPED at two-per-row width — a summary
 * tile is simply not where a five-word primary action belongs.
 *
 * The claim itself still cannot be completed anywhere but a scan, and that is
 * not a UX preference: nothing here knows WHICH physical unit the buyer is
 * holding. Nobody guessed at sale time — that is the point of the design — so
 * the scan is the resolver, and the only alternative is the brand telling us
 * which tag shipped.
 */
export const PendingSaleTeaser = ({ sale, compact = false }: PendingSaleTeaserProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const image = sale.product?.images?.[0]
  const title = sale.product?.title || 'Your purchase'
  const sku = sale.product?.sku ? formatSku(sale.product.sku) : null

  /*
   * `parseApiDate` first, NOT `new Date(raw)`.
   *
   * A Postgres timestamp arrives as `2026-08-27 10:04:11.219` when it is not
   * serialized through JSON, and **WebKit parses that as Invalid Date** — which
   * then throws out of date-fns' `format()` and takes the card down with it. On
   * iOS Safari, which is THE runtime for an app you reach by tapping an NFC tag
   * with a phone. `StatusNotification` records the same crash.
   */
  const purchasedDate = parseApiDate(sale.purchased_at)
  const purchased = purchasedDate ? formatedDate({ date: purchasedDate }) : null

  return (
    <Stack
      role='link'
      tabIndex={0}
      aria-label={`${title} — claim ownership`}
      onClick={() => navigate(ROUTES.PENDING_SALE.replace(':pendingSaleId', sale.id))}
      onKeyDown={event => {
        // The card is a div, so Enter/Space are not free. Without this the row
        // is reachable by keyboard and does nothing, which is worse than not
        // being focusable at all.
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(ROUTES.PENDING_SALE.replace(':pendingSaleId', sale.id))
        }
      }}
      sx={{
        // Same shell tokens as `CertificatesTeaser` — radius, hairline, the two
        // stacked shadows. A different card shape here would announce "this row
        // is a different kind of thing", which is exactly what must not happen.
        position: 'relative',
        height: '100%',
        cursor: 'pointer',
        background: theme.palette.common.white,
        // VRG-562: follows the tenant's Corner radius with the certificate card
        // it sits beside — the comment above is explicit that a different shape
        // here would announce a different kind of thing.
        borderRadius: surfaceRadius(theme),
        border: `1px solid ${theme.palette.grey[100]}`,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.10), 0 1px 2px rgba(16, 24, 40, 0.06)',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={compact ? 'column' : 'row'}
        sx={{
          padding: compact ? '12px' : '0 12px 0 0',
          minHeight: compact ? undefined : '112px',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: compact ? '100%' : '128px',
            aspectRatio: '1 / 1',
            // A fixed square in BOTH modes. The list card's photo is full-bleed
            // on the leading edge, so the card's own edge is the image.
            borderRadius: compact ? innerRadius : 0,
            overflow: 'hidden',
            backgroundColor: theme.palette.grey[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {image ? (
            // `style`, not `sx` — FadeInImage is a bare <img> wrapper and passes
            // geometry straight through (see its docstring).
            <FadeInImage
              src={image}
              alt={title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            // The SHARED empty state — same plate and glyph the platform's
            // tables use. Previously a bare grey[50] box with nothing in it.
            <ProductImagePlaceholder glyphSize={24} />
          )}
        </Box>

        <Stack
          sx={{
            flex: 1,
            minWidth: 0,
            justifyContent: 'center',
            gap: 0.5,
            pl: compact ? 0 : 1.5,
            pt: compact ? 1.5 : 1.5,
            pb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 500,
              color: 'text.primary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {title}
          </Typography>

          {sku && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }} noWrap>
              {sku}
            </Typography>
          )}

          <Box sx={{ mt: 0.5 }}>
            {/* `StatusBadge`, matching every sibling card in this grid — NOT the
                AuthenticChip the page floats on its photograph.

                Different question, different answer. The chip reports
                AUTHENTICITY, which matters on the certificate page because that
                page is reachable by a cloned or guessed URL. Nobody reaches THIS
                list except from their own account, so "Not verified" there
                answered a question no one asked and made a row where nothing is
                wrong read as though something were.

                The label is an ACTION because the card is tappable and goes
                straight to the claim (§ the label decision, 2026-08-31). */}
            <StatusBadge
              variant='chip'
              status='pending_ownership'
              label={PENDING_OWNERSHIP_CHIP.label}
              colors={PENDING_OWNERSHIP_CHIP.colors}
            />
          </Box>

          {purchased && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              Purchased {purchased}
            </Typography>
          )}

          {/*
            NO BUTTON HERE, and its removal is the point.

            "Scan to claim ownership" used to sit in this column. In the
            two-per-row grid that is roughly 180px wide, and the label rendered
            CLIPPED against the button's own edge — visible in the app, not in
            any typecheck. Widening the button or shortening the copy would have
            treated a placement problem as a styling one.

            A card in a grid is a SUMMARY. Every other row here opens a page to
            act on the thing; this one now does the same, and the claim lives on
            that page's state card where it has a full-width row to sit in. See
            `pages/PendingSalePage`.
          */}
        </Stack>
      </Stack>
    </Stack>
  )
}

export default PendingSaleTeaser
